import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, Calendar, User, Bookmark, Share2, MessageCircle, ThumbsUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArticleDetail = () => {
  const { slug } = useParams();
  const { isAuthenticated } = useAuth();
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentContent, setCommentContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [readingProgress, setReadingProgress] = useState(0);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const [articleRes, commentsRes] = await Promise.all([
          axios.get(`${API}/articles/${slug}`),
          axios.get(`${API}/comments/${slug}`).catch(() => ({ data: [] }))
        ]);
        setArticle(articleRes.data);
        setComments(commentsRes.data);
      } catch (error) {
        console.error('Error fetching article:', error);
        toast.error('Article not found');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const progress = (scrollTop / (documentHeight - windowHeight)) * 100;
      setReadingProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please login to comment');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/comments`,
        {
          article_id: article.article_id,
          content: commentContent
        },
        { withCredentials: true }
      );
      setComments([response.data, ...comments]);
      setCommentContent('');
      toast.success('Comment added!');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="article-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-900"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-serif font-bold text-charcoal mb-4">Article not found</h2>
          <Link to="/articles" className="text-gold hover:text-purple-700">
            Back to articles
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="article-detail-page">
      {/* Reading Progress Bar */}
      <div
        className="reading-progress"
        style={{ width: `${readingProgress}%` }}
      />

      {/* Hero */}
      <div className="relative py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-50 to-rose-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center space-x-2 text-sm text-charcoal/70 mb-6">
            <Link to="/articles" className="hover:text-gold">Articles</Link>
            <span>/</span>
            <span className="text-gold">{article.category}</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-charcoal mb-6" data-testid="article-title">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-charcoal/70">
            <div className="flex items-center space-x-2">
              <User className="h-5 w-5" />
              <span>By {article.author_name}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5" />
              <span>{article.reading_time} min read</span>
            </div>
            <button onClick={handleShare} className="flex items-center space-x-2 hover:text-gold" data-testid="share-button">
              <Share2 className="h-5 w-5" />
              <span>Share</span>
            </button>
          </div>
        </div>
      </div>

      {/* Cover Image */}
      {article.cover_image && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
          <img
            src={article.cover_image}
            alt={article.title}
            className="w-full h-96 object-cover rounded-3xl shadow-2xl"
            data-testid="article-cover-image"
          />
        </div>
      )}

      {/* Content */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-lg" data-testid="article-content" dangerouslySetInnerHTML={{ __html: article.content.replace(/\n/g, '<br/>').replace(/## /g, '<h2>').replace(/<h2>(.*?)<br\/>/g, '<h2>$1</h2>').replace(/### /g, '<h3>').replace(/<h3>(.*?)<br\/>/g, '<h3>$1</h3>') }} />
      </article>

      {/* Tags */}
      {article.tags && article.tags.length > 0 && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="flex flex-wrap gap-2">
            {article.tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-purple-100 text-gold rounded-full text-sm"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Comments */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-slate-200">
        <h2 className="text-3xl font-serif font-bold text-charcoal mb-8" data-testid="comments-section-title">
          Comments ({comments.length})
        </h2>

        {/* Comment Form */}
        {isAuthenticated ? (
          <form onSubmit={handleCommentSubmit} className="mb-12" data-testid="comment-form">
            <textarea
              value={commentContent}
              onChange={(e) => setCommentContent(e.target.value)}
              placeholder="Share your thoughts..."
              rows="4"
              className="w-full px-4 py-3 border border-gold rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
              data-testid="comment-input"
            />
            <button
              type="submit"
              className="mt-4 px-6 py-2 bg-gold-gradient text-white rounded-xl glow-gold-hover transition-colors"
              data-testid="comment-submit-button"
            >
              Post Comment
            </button>
          </form>
        ) : (
          <div className="mb-12 p-6 bg-slate-50 rounded-xl text-center">
            <p className="text-charcoal/70 mb-4">Join the conversation</p>
            <Link
              to="/login"
              className="px-6 py-2 bg-gold-gradient text-white rounded-xl glow-gold-hover transition-colors inline-block"
            >
              Log in to comment
            </Link>
          </div>
        )}

        {/* Comments List */}
        <div className="space-y-6">
          {comments.map((comment) => (
            <div key={comment.comment_id} className="p-6 bg-slate-50 rounded-xl" data-testid={`comment-${comment.comment_id}`}>
              <div className="flex items-start space-x-4">
                {comment.user_picture ? (
                  <img
                    src={comment.user_picture}
                    alt={comment.user_name}
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gold-gradient text-white flex items-center justify-center font-semibold">
                    {comment.user_name[0]}
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-charcoal">{comment.user_name}</span>
                    <span className="text-sm text-charcoal/70">
                      {new Date(comment.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-charcoal leading-relaxed">{comment.content}</p>
                  <div className="mt-3 flex items-center space-x-4 text-sm text-charcoal/70">
                    <button className="flex items-center space-x-1 hover:text-gold">
                      <ThumbsUp className="h-4 w-4" />
                      <span>{comment.likes}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ArticleDetail;
